import type { ReactNode } from "react";
import { Button, Checkbox, FormControlLabel, Grid, Radio, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useTransfertsEditionContext } from "../store/transfertsEditionContext";
import {
  TransfertsEditionActionType,
  TransfertsEditionReducerActionType,
} from "../store/transfertsEditionTypes";
import type { TransfertsEditionActionData } from "../store/transfertsEditionTypes";
import SaveIcon from "@mui/icons-material/Save";
import EpciAutocompleteWithErrorBoundary from "components/inputs/EpciAutocomplete";
import PerimetreAutoComplete from "components/inputs/PerimetreAutoComplete";

interface IGetInputDisplayAndValidatorResult {
  displays: {
    epci: boolean;
    perimetre: boolean;
    dateEffet: boolean;
    dateDebut: boolean;
    dateDeliberation: boolean;
    datePremierTransfert: boolean;
    dateAssembleeGenerale: boolean;
  };
  areInputsValids: (
    params: TransfertsEditionActionData,
    selectionLength: number,
    dateDebutPlusAncienTransfertSelectionne: Date | undefined
  ) => boolean;
}
function getInputDisplayAndValidator(
  type?: TransfertsEditionActionType
): IGetInputDisplayAndValidatorResult {
  const displayAndValidators: IGetInputDisplayAndValidatorResult = {
    displays: {
      epci: false,
      perimetre: false,
      dateEffet: true,
      dateDebut: false,
      dateDeliberation: false,
      datePremierTransfert: false,
      dateAssembleeGenerale: false,
    },
    areInputsValids: () => false,
  };

  switch (type) {
    case TransfertsEditionActionType.ReaffectationEPCI:
    case TransfertsEditionActionType.DirectIndirect:
      displayAndValidators.displays = {
        ...displayAndValidators.displays,
        epci: true,
      };
      displayAndValidators.areInputsValids = (params, selection) =>
        selection > 0 && params.dateEffet != null && params.idCible != null;
      break;
    case TransfertsEditionActionType.IndirectDirect:
      displayAndValidators.displays = {
        ...displayAndValidators.displays,
      };
      displayAndValidators.areInputsValids = (params, selection) =>
        selection > 0 && params.dateEffet != null && params.idCible == null;
      break;
    case TransfertsEditionActionType.ModificationDates:
      displayAndValidators.displays = {
        ...displayAndValidators.displays,
        dateEffet: false,
        dateDebut: true,
        dateDeliberation: true,
        datePremierTransfert: true,
        dateAssembleeGenerale: true,
      };
      displayAndValidators.areInputsValids = (
        { dateDebut, dateDeliberation, datePremierTransfert, dateAssembleeGenerale },
        selection,
        dateDebutPlusAncienTransfertSelectionne
      ) => {
        // La date de début de référence de validation est soit la date renseignée par l'utilisateur `dateDebut`
        // soit la date la plus ancienne de la sélection de transfert en cours `dateDebutPlusAncienTransfertSelectionne`.
        // La priorité est donc dateDebut >> dateDebutPlusAncienTransfertSelectionne.
        const dateReference = dateDebut ?? dateDebutPlusAncienTransfertSelectionne;
        let isValid = selection > 0;
        if (isValid) {
          isValid &&= [
            dateDebut,
            dateDeliberation,
            datePremierTransfert,
            dateAssembleeGenerale,
          ].some((d) => d != null);
        }

        if (isValid && datePremierTransfert != null) {
          isValid &&= dateReference == null ? true : datePremierTransfert <= dateReference;
        }
        if (isValid && dateDeliberation != null) {
          isValid &&= dateReference == null ? true : dateDeliberation <= dateReference;
        }

        return isValid;
      };

      break;
    case TransfertsEditionActionType.SuppressionTransfert:
      displayAndValidators.displays = {
        ...displayAndValidators.displays,
        dateEffet: false,
      };
      displayAndValidators.areInputsValids = (_, selection) => selection > 0;
      break;
    case TransfertsEditionActionType.ReaffectationPerimetre:
      displayAndValidators.displays = {
        ...displayAndValidators.displays,
        perimetre: true,
      };
      displayAndValidators.areInputsValids = (params, selection) =>
        selection > 0 && params.dateEffet != null && params.idCible != null;
      break;
  }

  return displayAndValidators;
}

function getFormControlStyle(
  value: TransfertsEditionActionType,
  actionUtilisateur: TransfertsEditionActionType | undefined
): SxProps<Theme> {
  let color = "";
  switch (value) {
    case TransfertsEditionActionType.SuppressionTransfert:
      color = "error.main";
      break;
    case actionUtilisateur:
      color = "info.main";
      break;
  }

  return {
    ".MuiFormControlLabel-label": {
      color,
      fontWeight: actionUtilisateur === value ? 600 : undefined,
    },
  };
}

/**
 * Clés/Valeurs de l'énum `TransfertsEditionActionType` utilisées pour construire la liste d'actions disponibles.
 */
const actionsPossibles = Object.entries(TransfertsEditionActionType).map(
  ([action, actionString]) => ({
    key: action,
    value: actionString,
  })
);

interface TransfertEditionToolbarProps {
  onSave: () => void;
  isSending: boolean;
}
function TransfertEditionToolbar({
  onSave,
  isSending,
}: Readonly<TransfertEditionToolbarProps>): ReactNode {
  const {
    state: {
      filteredTransferts,
      selectedTransferts,
      actionUtilisateur,
      actionParametres,
      sameCompetencePerimetres,
      dateDebutPlusAncienTransfertSelectionne,
      isLoading,
    },
    dispatch,
  } = useTransfertsEditionContext();

  const { displays, areInputsValids } = getInputDisplayAndValidator(actionUtilisateur);

  const transfertLength = filteredTransferts.length;
  const selectedLength = selectedTransferts.length;

  const canSave = areInputsValids(
    actionParametres,
    selectedLength,
    dateDebutPlusAncienTransfertSelectionne
  );

  return (
    <Grid
      container
      size={12}
      sx={{
        position: "sticky",
        top: "164px",
        zIndex: 99,
        backgroundColor: "white",
        borderRadius: 1,
        border: "1px solid rgba(224, 224, 224, 1)",
        px: 2,
        py: 1,
      }}>
      <Grid container spacing={1} size={12}>
        <Grid container size={12}>
          {actionsPossibles.map(({ key, value }) => (
            <Grid key={key} size={{ xs: 6, sm: 4, lg: "grow" }}>
              <FormControlLabel
                value={key}
                label={value}
                sx={getFormControlStyle(value, actionUtilisateur)}
                control={
                  <Radio
                    color={
                      value === TransfertsEditionActionType.SuppressionTransfert
                        ? "error"
                        : undefined
                    }
                    name="action-selectionnee"
                    checked={actionUtilisateur === value}
                    onChange={() => {
                      dispatch({
                        type: TransfertsEditionReducerActionType.USER_ACTION,
                        payload: value,
                      });
                    }}
                  />
                }
              />
            </Grid>
          ))}
        </Grid>
        {actionUtilisateur != null && (
          <Grid container size={12}>
            <Grid
              container
              sx={{ order: { xs: 3, lg: 1 }, alignContent: "center" }}
              size={{ xs: 6, lg: 2 }}>
              <Grid size={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      indeterminate={selectedLength > 0 && selectedLength < transfertLength}
                      checked={selectedLength > 0 && selectedLength === transfertLength}
                      onClick={() => {
                        const type =
                          selectedTransferts.length < filteredTransferts.length
                            ? TransfertsEditionReducerActionType.SELECT_ALL
                            : TransfertsEditionReducerActionType.UNSELECT_ALL;
                        dispatch({ type });
                      }}
                      sx={{ py: 0 }}
                    />
                  }
                  label={selectedLength < transfertLength ? "Tout cocher" : "Tout décocher"}
                />
              </Grid>
              <Grid size={12}>
                <Typography variant="caption">
                  {selectedLength} sélectionné{selectedLength > 1 ? "s" : ""} sur {transfertLength}
                </Typography>
              </Grid>
            </Grid>

            <Grid sx={{ order: { xs: 1, lg: 2 } }} size={{ xs: 12, lg: "grow" }} container>
              {displays.epci && (
                <Grid size={{ xs: 12, sm: 6, lg: 8 }}>
                  <EpciAutocompleteWithErrorBoundary
                    key={`${actionUtilisateur}-epci`}
                    onChange={(epci) => {
                      dispatch({
                        type: TransfertsEditionReducerActionType.USER_PARAMETRES,
                        payload: { idCible: epci?.id },
                      });
                    }}
                  />
                </Grid>
              )}
              {displays.perimetre && (
                <Grid size={{ xs: 12, sm: 6, lg: 8 }}>
                  <PerimetreAutoComplete
                    key={`${actionUtilisateur}-perimetre`}
                    onChange={(perimetre) => {
                      dispatch({
                        type: TransfertsEditionReducerActionType.USER_PARAMETRES,
                        payload: { idCible: perimetre?.id },
                      });
                    }}
                    perimetres={sameCompetencePerimetres}
                    isLoading={isLoading}
                  />
                </Grid>
              )}
              {displays.dateDebut && (
                <Grid size={{ xs: 12, sm: 6, lg: "grow" }}>
                  <DatePicker
                    key={`${actionUtilisateur}-date-debut`}
                    label="Date d'effet"
                    format="dd/MM/yyyy"
                    onChange={(dateDebut: Date | null) => {
                      dispatch({
                        type: TransfertsEditionReducerActionType.USER_PARAMETRES,
                        payload: { dateDebut: dateDebut ?? undefined },
                      });
                    }}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
              )}
              {displays.dateDeliberation && (
                <Grid size={{ xs: 12, sm: 6, lg: "grow" }}>
                  <DatePicker
                    key={`${actionUtilisateur}-date-deliberation`}
                    label="Délibération"
                    format="dd/MM/yyyy"
                    onChange={(dateDeliberation: Date | null) => {
                      dispatch({
                        type: TransfertsEditionReducerActionType.USER_PARAMETRES,
                        payload: { dateDeliberation: dateDeliberation ?? undefined },
                      });
                    }}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
              )}
              {displays.datePremierTransfert && (
                <Grid size={{ xs: 12, sm: 6, lg: "grow" }}>
                  <DatePicker
                    key={`${actionUtilisateur}-date-premier-transfert`}
                    label="Début de transfert"
                    format="dd/MM/yyyy"
                    onChange={(datePremierTransfert: Date | null) => {
                      dispatch({
                        type: TransfertsEditionReducerActionType.USER_PARAMETRES,
                        payload: { datePremierTransfert: datePremierTransfert ?? undefined },
                      });
                    }}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
              )}
              {displays.dateAssembleeGenerale && (
                <Grid size={{ xs: 12, sm: 6, lg: "grow" }}>
                  <DatePicker
                    key={`${actionUtilisateur}-date-assemblee-generale`}
                    label="Assemblée générale"
                    format="dd/MM/yyyy"
                    onChange={(dateAssembleeGenerale: Date | null) => {
                      dispatch({
                        type: TransfertsEditionReducerActionType.USER_PARAMETRES,
                        payload: { dateAssembleeGenerale: dateAssembleeGenerale ?? undefined },
                      });
                    }}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
              )}
              {displays.dateEffet && (
                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                  <DatePicker
                    key={`${actionUtilisateur}-date-effet`}
                    label="Date d'effet *"
                    format="dd/MM/yyyy"
                    onChange={(dateEffet: Date | null) => {
                      dispatch({
                        type: TransfertsEditionReducerActionType.USER_PARAMETRES,
                        payload: { dateEffet: dateEffet ?? undefined },
                      });
                    }}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
              )}
            </Grid>
            <Grid
              container
              sx={{ justifyContent: "flex-end", order: { xs: 4, lg: 4 } }}
              size={{ xs: 6, lg: "auto" }}>
              <Grid>
                <Button
                  size="large"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  fullWidth={true}
                  onClick={onSave}
                  disabled={!canSave}
                  loading={isSending}
                  loadingPosition="start">
                  Confirmer
                </Button>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}

export default TransfertEditionToolbar;
